import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";

const PROOF_SLIDES = [
  {
    src: "/proof/job-offer.png",
    tag: "LINKEDIN",
    title: "6-figure infra role + equity",
    accent: "#00F0FF",
  },
  {
    src: "/proof/private-equity.png",
    tag: "DM",
    title: "Strategic equity partnership",
    accent: "#00F0FF",
  },
  {
    src: "/proof/law-firm.png",
    tag: "DM",
    title: "Inbound law firm consulting",
    accent: "#00F0FF",
  },
  {
    src: "/proof/acquisition.png",
    tag: "DM",
    title: "Acquisition · 3-4× profit",
    accent: "#D4AF37",
  },
  {
    src: "/proof/sponsorship.png",
    tag: "EMAIL",
    title: "Paid sponsorship deal",
    accent: "#D4AF37",
    // Source is unusually tall — cap so it visually matches the other cards
    mediaMaxHeight: 440,
  },
];

export default function ProofCarousel() {
  const trackRef = useRef(null);
  const sectionRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [dragging, setDragging] = useState(false);
  const hoveredRef = useRef(false);
  const draggingRef = useRef(false);
  const indexRef = useRef(0);
  useEffect(() => {
    indexRef.current = index;
  }, [index]);
  useEffect(() => {
    draggingRef.current = dragging;
  }, [dragging]);

  const programmaticScrollRef = useRef(false);
  const lastInteractionRef = useRef(0);
  const PAUSE_AFTER_INTERACTION_MS = 9000;

  const scrollToIndex = useCallback((i) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[i];
    if (!card) return;
    setIndex(i);
    lastInteractionRef.current = Date.now();
    programmaticScrollRef.current = true;
    const left = card.offsetLeft - track.offsetLeft;
    track.scrollTo({ left, behavior: "smooth" });
    window.setTimeout(() => {
      programmaticScrollRef.current = false;
    }, 700);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let raf;
    const onScroll = () => {
      if (programmaticScrollRef.current) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const center = track.scrollLeft + track.clientWidth / 2;
        let closest = 0;
        let closestDist = Infinity;
        for (let i = 0; i < track.children.length; i++) {
          const c = track.children[i];
          const mid = c.offsetLeft + c.offsetWidth / 2 - track.offsetLeft;
          const d = Math.abs(mid - center);
          if (d < closestDist) {
            closestDist = d;
            closest = i;
          }
        }
        setIndex(closest);
      });
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      track.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Drag-to-scroll on desktop
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let isDown = false;
    let startX = 0;
    let startScroll = 0;

    const onDown = (e) => {
      if (e.target.closest("a, button")) return;
      isDown = true;
      setDragging(true);
      track.classList.add("aos-grabbing");
      startX = (e.pageX || e.touches?.[0]?.pageX) - track.offsetLeft;
      startScroll = track.scrollLeft;
    };
    const onMove = (e) => {
      if (!isDown) return;
      const x = (e.pageX || e.touches?.[0]?.pageX) - track.offsetLeft;
      const walk = (x - startX) * 1.1;
      track.scrollLeft = startScroll - walk;
    };
    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      setDragging(false);
      track.classList.remove("aos-grabbing");
    };

    track.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    track.addEventListener("mouseleave", onUp);
    return () => {
      track.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      track.removeEventListener("mouseleave", onUp);
    };
  }, []);

  const total = PROOF_SLIDES.length;
  const prev = () => scrollToIndex(Math.max(0, index - 1));
  const next = () => scrollToIndex(Math.min(total - 1, index + 1));

  // Desktop-only auto-advance. Pauses while the user is hovering, dragging,
  // or if the OS requests reduced motion. Uses refs so the timer doesn't
  // restart on every state change.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mqDesktop = window.matchMedia("(min-width: 768px)");
    const mqReduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!mqDesktop.matches || mqReduced.matches) return;

    const id = setInterval(() => {
      if (draggingRef.current) return;
      // Brief pause after a manual prev/next/dot click so we don't yank
      // the carousel from under the user.
      if (Date.now() - lastInteractionRef.current < PAUSE_AFTER_INTERACTION_MS) return;
      const nextIdx = (indexRef.current + 1) % total;
      const track = trackRef.current;
      if (track) {
        const card = track.children[nextIdx];
        if (card) {
          programmaticScrollRef.current = true;
          const left = card.offsetLeft - track.offsetLeft;
          track.scrollTo({ left, behavior: "smooth" });
          window.setTimeout(() => {
            programmaticScrollRef.current = false;
          }, 700);
        }
      }
      setIndex(nextIdx);
    }, 4500);

    return () => clearInterval(id);
  }, [total]);

  return (
    <section
      ref={sectionRef}
      data-testid="proof-section"
      className="section-dark grain py-24 md:py-32 border-t border-white/5"
    >
      <div
        className="orb orb-blue"
        style={{ width: 620, height: 620, top: "-180px", left: "-200px" }}
      />
      <div
        className="orb orb-gold"
        style={{ width: 380, height: 380, bottom: "-140px", right: "-100px" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="aos-receipts-pill">
              <span className="aos-receipts-dot" />
              Receipts
            </span>
            <h2 className="font-display font-black text-white leading-[1.02] text-[clamp(2.25rem,5vw,4.25rem)] mt-5">
              Real inboxes.{" "}
              <span className="text-[#00F0FF]">Real offers.</span>
            </h2>
            <p className="text-white/65 text-base md:text-lg leading-relaxed mt-4 max-w-xl">
              Acquisition offers, six-figure job offers, paid sponsorships,
              inbound from law firms — pulled straight from the inbox. Swipe
              through.
            </p>
          </div>
        </div>

        {/* Track */}
        <div
          ref={trackRef}
          data-testid="proof-track"
          className={`aos-proof-track ${dragging ? "aos-grabbing" : ""}`}
        >
          {PROOF_SLIDES.map((s, i) => (
            <ProofCard
              key={s.src}
              slide={s}
              index={i}
              total={total}
              active={i === index}
              onClick={() => scrollToIndex(i)}
              testid={`proof-card-${i}`}
            />
          ))}
        </div>

        {/* Footer controls */}
        <div className="mt-10 flex items-center justify-center gap-5">
          <CarouselArrow
            onClick={prev}
            disabled={index === 0}
            direction="left"
            testid="proof-prev"
          />
          <div className="flex items-center gap-2">
            {PROOF_SLIDES.map((_, i) => (
              <button
                key={i}
                data-testid={`proof-dot-${i}`}
                onClick={() => scrollToIndex(i)}
                aria-label={`Show proof ${i + 1}`}
                className="aos-proof-dot"
                style={{
                  background:
                    i === index ? "#00F0FF" : "rgba(255,255,255,0.18)",
                  transform: i === index ? "scale(1.15)" : "scale(1)",
                  boxShadow:
                    i === index
                      ? "0 0 0 4px rgba(0,240,255,0.12)"
                      : "none",
                }}
              />
            ))}
          </div>
          <CarouselArrow
            onClick={next}
            disabled={index === total - 1}
            direction="right"
            testid="proof-next"
          />
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 text-[10px] tracking-[0.32em] uppercase text-white/35">
          <Lock size={10} /> Real Inbounds · Names Removed for Privacy
        </div>
      </div>
    </section>
  );
}

function ProofCard({ slide, index, total, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      className="aos-proof-card"
      style={{
        transform: active ? "scale(1)" : "scale(0.95)",
        filter: active ? "none" : "saturate(0.9)",
      }}
    >
      <div
        className="aos-proof-shell"
        style={{
          borderColor: active ? `${slide.accent}55` : "rgba(255,255,255,0.10)",
          boxShadow: active
            ? `0 24px 60px rgba(0,0,0,0.55), 0 0 40px ${slide.accent}20`
            : "0 12px 32px rgba(0,0,0,0.4)",
        }}
      >
        <div className="aos-proof-header" />
        <div
          className="aos-proof-media"
          style={
            slide.mediaMaxHeight
              ? { maxHeight: slide.mediaMaxHeight, overflow: "hidden" }
              : undefined
          }
        >
          <img
            src={slide.src}
            alt={slide.title}
            loading="lazy"
            draggable={false}
            style={
              slide.mediaMaxHeight
                ? {
                    width: "100%",
                    height: slide.mediaMaxHeight,
                    objectFit: "cover",
                    objectPosition: "top center",
                  }
                : undefined
            }
          />
        </div>
        <div className="aos-proof-meta">
          <span
            className="aos-proof-meta-tag"
            style={{
              color: slide.accent,
              borderColor: `${slide.accent}55`,
              background: `${slide.accent}14`,
            }}
          >
            {slide.tag}
          </span>
          <div className="aos-proof-meta-row">
            <div className="aos-proof-meta-title">{slide.title}</div>
            <div className="aos-proof-meta-index">
              {index + 1} / {total}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function CarouselArrow({ onClick, disabled, direction, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      aria-label={direction === "left" ? "Previous proof" : "Next proof"}
      className="aos-proof-arrow"
      style={{
        opacity: disabled ? 0.3 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {direction === "left" ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
    </button>
  );
}
